import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Scale, Users, MessageSquare, LogOut,
  ChevronRight, Search, X, CheckCircle2, Home, FileText,
  Shield, Plus, Eye, ArrowLeft, UserCircle, Building2, Star,
  ThumbsUp, ThumbsDown, Bot, ChevronDown, Palette, Banknote,
} from "lucide-react";
import { MediaLightbox } from "../../components/MediaLightbox";
import EditPageEditor from "../../components/editor/EditPageEditor";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "orello_token";

function adminHeaders() {
  return { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
}
async function req(method, path, body) {
  const res = await fetch(`${API}${path}`, {
    method, headers: adminHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Request failed");
  }
  return res.json();
}

function formatINR(paise) {
  if (paise == null) return "—";
  return `₹${(paise / 100).toLocaleString("en-IN")}`;
}
function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const ROLE_LABELS = {
  tenant: "Tenant", landlord: "Landlord", agent: "Agent",
  broker_admin: "Broker Admin", orello_admin: "Orello Admin",
};
const ROLE_COLORS = {
  tenant:       "bg-sky-50 text-sky-700 border-sky-200",
  landlord:     "bg-violet-50 text-violet-700 border-violet-200",
  agent:        "bg-teal-50 text-teal-700 border-teal-200",
  broker_admin: "bg-indigo-50 text-indigo-700 border-indigo-200",
  orello_admin: "bg-orange-50 text-orange-700 border-orange-200",
};
const STATUS_MAP = {
  pending:         { label: "Pending",         cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ore_reviewed:    { label: "ORE-AI Reviewed", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  manual_review:   { label: "Manual Review",   cls: "bg-orange-50 text-orange-700 border-orange-200" },
  manual_resolved: { label: "Final Decision",  cls: "bg-purple-50 text-purple-700 border-purple-200" },
  resolved:        { label: "Resolved",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  external:        { label: "External",        cls: "bg-gray-100 text-gray-600 border-gray-200" },
  open:            { label: "Open",            cls: "bg-red-50 text-red-700 border-red-200" },
  in_progress:     { label: "In Progress",     cls: "bg-amber-50 text-amber-700 border-amber-200" },
  verified:        { label: "Verified",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  submitted:       { label: "Submitted",       cls: "bg-blue-50 text-blue-700 border-blue-200" },
  active:          { label: "Active",          cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  completed:       { label: "Completed",       cls: "bg-gray-100 text-gray-600 border-gray-200" },
  cancelled:       { label: "Cancelled",       cls: "bg-red-50 text-red-700 border-red-200" },
  not_started:     { label: "Not Started",     cls: "bg-gray-100 text-gray-400 border-gray-200" },
  none:            { label: "No KYC",          cls: "bg-gray-100 text-gray-400 border-gray-200" },
  rejected:        { label: "Rejected",        cls: "bg-red-50 text-red-700 border-red-200" },
  signed:          { label: "Signed",          cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awaiting:        { label: "Awaiting",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
function Badge({ status }) {
  const roleStyle = ROLE_COLORS[status];
  if (roleStyle) return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleStyle}`}>
      {ROLE_LABELS[status] || status}
    </span>
  );
  const s = STATUS_MAP[status] || { label: status || "—", cls: "bg-gray-100 text-gray-500 border-gray-200" };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>{s.label}</span>;
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const NAV = [
  { id: "overview",  label: "Overview",  Icon: LayoutDashboard },
  { id: "disputes",  label: "Disputes",  Icon: Scale },
  { id: "disbursements", label: "Disbursements", Icon: Banknote },
  { id: "users",     label: "Users",     Icon: Users },
  { id: "support",   label: "Support",   Icon: MessageSquare },
  { id: "feedback",  label: "Feedback",  Icon: Star },
  { id: "editor",    label: "Page Editor", Icon: Palette },
];

function AdminSidebar({ active, setActive, onLogout, stats }) {
  return (
    <aside style={{
      width: 220, flexShrink: 0, background: "var(--dash-card)",
      borderRight: "1px solid var(--dash-border)",
      display: "flex", flexDirection: "column", minHeight: "100vh",
    }}>
      <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--dash-border)" }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: "var(--dash-text)" }}>Orello</div>
        <div style={{ fontSize: 11, color: "var(--dash-text-2)", marginTop: 2 }}>Admin Dashboard</div>
      </div>
      <nav style={{ flex: 1, padding: "12px 8px" }}>
        {NAV.map(({ id, label, Icon }) => {
          const isActive = active === id;
          const badge = id === "disputes" ? stats?.pending_disputes
                      : id === "support"  ? stats?.open_support_tickets : null;
          return (
            <button key={id} onClick={() => setActive(id)} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 10,
              padding: "9px 12px", borderRadius: 10, border: "none", cursor: "pointer",
              marginBottom: 2, fontSize: 13, fontWeight: isActive ? 600 : 400,
              background: isActive ? "rgba(249,115,22,0.08)" : "transparent",
              color: isActive ? "#F97316" : "var(--dash-text-2)",
              transition: "all 0.15s",
            }}>
              <Icon size={16} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: "left" }}>{label}</span>
              {badge > 0 && (
                <span style={{
                  background: "#F97316", color: "#fff", fontSize: 10,
                  fontWeight: 700, borderRadius: 99, padding: "1px 6px",
                }}>{badge}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div style={{ padding: "12px 8px", borderTop: "1px solid var(--dash-border)" }}>
        <button onClick={onLogout} style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "9px 12px", borderRadius: 10, border: "none", cursor: "pointer",
          background: "transparent", color: "#ef4444", fontSize: 13,
        }}>
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </aside>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, onClick }) {
  const colors = {
    blue:   { bg: "bg-blue-50",   text: "text-blue-600",   icon: "text-blue-500" },
    green:  { bg: "bg-emerald-50",text: "text-emerald-600",icon: "text-emerald-500" },
    orange: { bg: "bg-orange-50", text: "text-orange-600", icon: "text-orange-500" },
    red:    { bg: "bg-red-50",    text: "text-red-600",    icon: "text-red-500" },
    yellow: { bg: "bg-amber-50",  text: "text-amber-600",  icon: "text-amber-500" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", icon: "text-purple-500" },
  };
  const c = colors[color] || colors.blue;
  return (
    <button onClick={onClick}
      className={`card-base p-5 text-left w-full transition-all ${onClick ? "hover:border-[#F97316]/40 hover:shadow-md cursor-pointer" : "cursor-default"}`}>
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${c.bg} mb-3`}>
        <Icon className={`h-5 w-5 ${c.icon}`} />
      </div>
      <div className={`text-3xl font-bold font-display ${c.text}`}>{value ?? "—"}</div>
      <div className="text-sm text-gray-500 mt-1 flex items-center justify-between">
        {label}
        {onClick && <ChevronRight className="h-3.5 w-3.5 text-gray-300" />}
      </div>
    </button>
  );
}

function Overview({ stats, setActive }) {
  return (
    <div>
      <h1 className="text-2xl font-bold font-display mb-6">Overview</h1>
      <div className="grid grid-cols-3 gap-4">
        <StatCard icon={Users}         label="Total Users"          value={stats?.total_users}          color="blue"   onClick={() => setActive("users")} />
        <StatCard icon={Home}          label="Active Rentals"       value={stats?.active_rentals}       color="green"  onClick={() => setActive("users")} />
        <StatCard icon={Scale}         label="Pending Disputes"     value={stats?.pending_disputes}     color="orange" onClick={() => setActive("disputes")} />
        <StatCard icon={MessageSquare} label="Open Support Tickets" value={stats?.open_support_tickets} color="red"    onClick={() => setActive("support")} />
        <StatCard icon={Shield}        label="KYC Pending"          value={stats?.kyc_pending}          color="yellow" onClick={() => setActive("users")} />
        <StatCard icon={FileText}      label="Total Rentals"        value={stats?.total_rentals}        color="purple" onClick={() => setActive("users")} />
      </div>
    </div>
  );
}

// ─── Report view helper ────────────────────────────────────────────────────────
const CONDITION_COLOR = {
  good:      "bg-emerald-100 text-emerald-700",
  excellent: "bg-emerald-100 text-emerald-700",
  fair:      "bg-amber-100 text-amber-700",
  average:   "bg-amber-100 text-amber-700",
  poor:      "bg-red-100 text-red-700",
  damaged:   "bg-red-100 text-red-700",
};

function ConditionPill({ condition }) {
  const cls = CONDITION_COLOR[condition?.toLowerCase()] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>
      {condition || "—"}
    </span>
  );
}

// Single expandable room row
function RoomRow({ rm, onOpenLightbox }) {
  const [expanded, setExpanded] = useState(false);
  const photos  = rm.photo_urls || [];
  const isVideo = (url) => /\.(mp4|mov|webm|avi)/i.test(url) || url?.includes("/video/");

  return (
    <div className="border-b border-gray-50 last:border-0">
      {/* Row header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
          <span className="text-sm text-gray-700 font-medium truncate">{rm.room}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {photos.length > 0
            ? <span className="text-xs text-[#F97316] font-medium">📷 {photos.length}</span>
            : <span className="text-xs text-gray-400">📷 —</span>
          }
          <ConditionPill condition={rm.condition} />
        </div>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="pb-3 px-6 space-y-2.5">
          {rm.notes && <p className="text-xs text-gray-500 italic">{rm.notes}</p>}

          {photos.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((url, i) =>
                isVideo(url) ? (
                  <div key={i} onClick={() => onOpenLightbox(photos, i)}
                    className="relative w-full aspect-video bg-black rounded-xl overflow-hidden cursor-pointer group">
                    <video src={url} className="w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm group-hover:bg-white/30 transition-all">
                        <span className="text-white text-lg ml-0.5">▶</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button key={i} onClick={() => onOpenLightbox(photos, i)}
                    className="w-full aspect-video rounded-xl overflow-hidden border border-gray-100 hover:border-[#F97316]/40 hover:scale-[1.02] transition-all">
                    <img src={url} alt={`${rm.room} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                )
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-5 text-center">
              <svg className="h-6 w-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 18h16.5M12 9.75h.008v.008H12V9.75z" />
              </svg>
              <p className="text-xs text-gray-400 leading-snug">No photos attached<br />by broker / agent</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportView({ report }) {
  const [lightbox, setLightbox] = useState(null); // { items, idx }
  const isVideo = (url) => /\.(mp4|mov|webm|avi)/i.test(url) || url?.includes("/video/");

  if (!report) return <div className="text-sm text-gray-400">Not submitted yet.</div>;

  const rooms = report.rooms || [];
  const topPhotos = report.photo_urls || [];

  return (
    <>
      {lightbox && (
        <MediaLightbox items={lightbox.items} startIdx={lightbox.idx} onClose={() => setLightbox(null)} />
      )}

      <div>
        {/* Meta row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {report.key_handover_confirmed !== undefined && (
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${report.key_handover_confirmed ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
              🔑 {report.key_handover_confirmed ? "Keys handed over" : "Keys not confirmed"}
            </span>
          )}
          {topPhotos.length > 0 && (
            <span className="text-xs text-[#F97316] font-medium">
              {topPhotos.length} general photo{topPhotos.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Room rows */}
        {rooms.length > 0 ? (
          <div>
            {rooms.map((rm, i) => (
              <RoomRow key={i} rm={rm} onOpenLightbox={(items, idx) => setLightbox({ items, idx })} />
            ))}
          </div>
        ) : (
          // Legacy flat format
          <div className="space-y-1.5">
            {[
              ["Walls",      report.walls_condition,     report.walls_notes],
              ["Floor",      report.flooring_condition,  report.flooring_notes],
              ["Bathroom",   report.bathroom_condition,  report.bathroom_notes],
              ["Kitchen",    report.kitchen_condition,   report.kitchen_notes],
              ["Fixtures",   report.fixtures_condition,  report.fixtures_notes],
              ["Appliances", report.appliances_condition,report.appliances_notes],
            ].filter(([, c]) => c).map(([label, cond, notes]) => (
              <div key={label} className="flex items-start justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-700">{label}</span>
                <div className="flex flex-col items-end gap-1">
                  <ConditionPill condition={cond} />
                  {notes && <span className="text-xs text-gray-500 italic text-right">{notes}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Overall notes */}
        {report.overall_notes && (
          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 italic">{report.overall_notes}</div>
        )}

        {/* General photos (report-level, not room-specific) */}
        {topPhotos.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">General Photos</div>
            <div className="grid grid-cols-3 gap-2">
              {topPhotos.map((url, i) => (
                isVideo(url) ? (
                  <div key={i} onClick={() => setLightbox({ items: topPhotos, idx: i })}
                    className="relative aspect-video bg-black rounded-xl overflow-hidden cursor-pointer group">
                    <video src={url} className="w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-white">▶</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button key={i} onClick={() => setLightbox({ items: topPhotos, idx: i })}
                    className="aspect-video rounded-xl overflow-hidden border border-gray-100 hover:scale-[1.02] transition-all">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Admin Rental Detail ───────────────────────────────────────────────────────
function AdminRentalDetail({ rid, onBack }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    req("GET", `/admin/rentals/${rid}`).then(setData).catch(e => toast.error(e.message));
  }, [rid]);

  if (!data) return <div className="text-sm p-8 text-center" style={{ color: "var(--dash-text-2)" }}>Loading rental…</div>;

  const { rental, tenant_users, landlord, agent, agreement, checkin, checkout, settlement, dispute, deduction, counter } = data;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <Badge status={rental.status} />
        <span className="text-gray-400 text-xs font-mono">#{rental.id?.slice(-8)}</span>
      </div>

      {/* Property */}
      <div className="card-base p-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{rental.property_address}</h2>
            <div className="text-sm text-gray-500 mt-1">
              {rental.property_type}
              {rental.rent_amount ? ` · ₹${(rental.rent_amount / 100).toLocaleString("en-IN")}/mo` : ""}
              {" · Deposit "}{formatINR(rental.deposit_amount)}
            </div>
            <div className="text-xs text-gray-400 mt-1">Created {fmtDate(rental.created_at)}</div>
          </div>
        </div>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">
            Tenant{tenant_users.length > 1 ? "s" : ""}
          </div>
          {tenant_users.map((tu, i) => (
            <div key={i} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b border-gray-50 last:border-0">
              <div className="font-medium text-sm text-gray-900">{tu.user.name || "Invited"}</div>
              <div className="font-mono text-xs text-gray-400">{tu.user.phone || tu.tenant.phone}</div>
              <div className="text-xs text-gray-400 mt-0.5">Share: {formatINR(tu.tenant.tenant_share_paise)}</div>
              <div className="mt-1"><Badge status={tu.tenant.payment_status || "pending"} /></div>
            </div>
          ))}
        </div>

        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">Landlord</div>
          <div className="font-medium text-sm text-gray-900">{landlord.name || "—"}</div>
          <div className="font-mono text-xs text-gray-400">{landlord.phone}</div>
          <div className="text-xs text-gray-400 mt-0.5">{landlord.email}</div>
        </div>

        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">Agent / Broker</div>
          {agent?.id ? (
            <>
              <div className="font-medium text-sm text-gray-900">{agent.name}</div>
              <div className="font-mono text-xs text-gray-400">{agent.phone}</div>
              <div className="mt-1"><Badge status={agent.role} /></div>
            </>
          ) : <div className="text-gray-400 text-sm">—</div>}
        </div>
      </div>

      {/* Agreement */}
      {agreement?.id && (
        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">Agreement</div>
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <span className="text-gray-600">Move-in: <strong className="text-gray-900">{fmtDate(agreement.move_in_date)}</strong></span>
            <span className="text-gray-600">Move-out: <strong className="text-gray-900">{fmtDate(agreement.move_out_date)}</strong></span>
            <Badge status={agreement.status} />
          </div>
        </div>
      )}

      {/* Check-in / Checkout */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card-base p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Check-in Report</div>
            {checkin && <span className="text-xs text-gray-400">{fmtDate(checkin.submitted_at || checkin.created_at)}</span>}
          </div>
          {checkin
            ? <ReportView report={checkin} />
            : <div className="text-sm text-gray-400">Not submitted yet.</div>
          }
        </div>
        <div className="card-base p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Checkout Report</div>
            {checkout && <span className="text-xs text-gray-400">{fmtDate(checkout.submitted_at || checkout.created_at)}</span>}
          </div>
          {checkout
            ? <ReportView report={checkout} />
            : <div className="text-sm text-gray-400">Not submitted yet.</div>
          }
        </div>
      </div>

      {/* Deduction + Counter */}
      {deduction && (
        <div className="card-base p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Landlord's Deduction</div>
            <Badge status={deduction.status} />
          </div>
          {deduction.items?.map((item, i) => (
            <div key={i} className="flex justify-between text-sm py-2 border-b border-gray-50 last:border-0">
              <span className="text-gray-600">{item.room} — {item.reason}</span>
              <span className="font-medium text-gray-900">{formatINR(item.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between font-semibold text-sm pt-2">
            <span className="text-gray-600">Total claimed</span>
            <span className="text-[#F97316]">{formatINR(deduction.total_deduction)}</span>
          </div>

          {counter && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">Tenant's Counter-proposal</div>
              {counter.items?.map((item, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5">
                  <span className="text-gray-600">{item.room} — {item.reason}</span>
                  <span className="font-medium text-emerald-600">{formatINR(item.proposed_amount)}</span>
                </div>
              ))}
              {counter.reason && <div className="text-xs text-gray-500 mt-2 italic">"{counter.reason}"</div>}
            </div>
          )}
        </div>
      )}

      {/* Dispute */}
      {dispute && (
        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="text-xs text-orange-600 uppercase font-semibold tracking-wide">Dispute</div>
            <Badge status={dispute.status} />
          </div>
          <div className="text-sm text-gray-800">{dispute.reason}</div>
          {dispute.orello_suggestion && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="text-xs text-blue-600 font-semibold mb-1">🤖 ORE-AI Verdict</div>
              <div className="text-sm text-blue-800">{dispute.orello_suggestion}</div>
              <div className="text-xs text-blue-500 mt-1">
                Suggested deduction: {formatINR(dispute.orello_deduction_suggestion)} ·
                Tenant refund: {formatINR(dispute.orello_refund_suggestion)}
              </div>
            </div>
          )}
          {dispute.ore_ai_item_analysis?.length > 0 && (
            <div>
              <div className="text-xs text-gray-400 font-semibold uppercase mb-2">Item Analysis</div>
              <div className="grid grid-cols-3 text-xs text-gray-400 font-medium pb-1 border-b border-orange-100">
                <span>Item</span><span className="text-right">Claimed</span><span className="text-right">Deducted</span>
              </div>
              {dispute.ore_ai_item_analysis.map((a, i) => (
                <div key={i} className="grid grid-cols-3 py-1.5 border-b border-orange-50 last:border-0 text-sm">
                  <span className="text-gray-700">{a.room ? `${a.room} — ` : ""}{a.item}</span>
                  <span className="text-right text-gray-500">{formatINR(a.claimed)}</span>
                  <span className={`text-right font-medium ${a.awarded === 0 ? "text-emerald-600" : a.awarded < a.claimed ? "text-amber-600" : "text-red-600"}`}>
                    {a.awarded === 0 ? "Nil" : formatINR(a.awarded)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {dispute.manual_review_verdict && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
              <div className="text-xs text-purple-600 font-semibold mb-1">Final Decision (Orello Team)</div>
              <div className="text-sm text-purple-800 font-medium">{dispute.manual_review_verdict}</div>
              {dispute.manual_review_reasoning && (
                <div className="text-xs text-purple-500 mt-1">{dispute.manual_review_reasoning}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settlement */}
      {settlement && (
        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">Settlement</div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="text-xs text-gray-500 mb-1">Deposit</div>
              <div className="font-bold text-gray-900">{formatINR(rental.deposit_amount)}</div>
            </div>
            <div className="bg-red-50 rounded-xl p-3">
              <div className="text-xs text-red-500 mb-1">Deducted</div>
              <div className="font-bold text-red-600">{formatINR(settlement.landlord_deduction_amount)}</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3">
              <div className="text-xs text-emerald-500 mb-1">Refunded</div>
              <div className="font-bold text-emerald-600">{formatINR(settlement.tenant_refund_amount)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── User Detail ───────────────────────────────────────────────────────────────
// ─── Chatbot history card inside UserDetail ───────────────────────────────────
function ChatbotHistory({ userId }) {
  const [convos, setConvos]   = useState(null);
  const [openId, setOpenId]   = useState(null);

  useEffect(() => {
    req("GET", `/admin/users/${userId}/chatbot`).then(setConvos).catch(() => setConvos([]));
  }, [userId]);

  if (convos === null) return (
    <div className="card-base p-4 text-sm text-gray-400">Loading chat history…</div>
  );

  return (
    <div className="card-base p-5">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="h-4 w-4 text-indigo-500" />
        <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">
          Chatbot Conversations ({convos.length})
        </div>
      </div>
      {convos.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-4">No chatbot conversations yet.</div>
      ) : (
        <div className="space-y-2">
          {convos.map(c => {
            const msgs    = c.messages || [];
            const userQ   = msgs.filter(m => m.role === "user").length;
            const preview = msgs.find(m => m.role === "user")?.content || "—";
            const isOpen  = openId === c.conversation_id;
            return (
              <div key={c.conversation_id} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Row header */}
                <button
                  onClick={() => setOpenId(isOpen ? null : c.conversation_id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-700 font-medium truncate">{preview}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {userQ} message{userQ !== 1 ? "s" : ""} · {timeAgo(c.updated_at || c.created_at)}
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>

                {/* Expanded thread */}
                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50/40 px-4 py-3 space-y-3 max-h-80 overflow-y-auto">
                    {msgs.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                          m.role === "user"
                            ? "bg-[#F97316] text-white"
                            : "bg-white border border-gray-200 text-gray-700"
                        }`}>
                          {m.role === "assistant" && (
                            <div className="flex items-center gap-1 text-indigo-500 font-semibold mb-1 text-[10px] uppercase tracking-wide">
                              <Bot className="h-3 w-3" /> Orello AI
                            </div>
                          )}
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserDetail({ userId, onBack }) {
  const [data, setData]         = useState(null);
  const [editing, setEditing]   = useState(false);
  const [viewRental, setViewRental] = useState(null);

  const load = useCallback(() => {
    req("GET", `/admin/users/${userId}`).then(setData).catch(e => toast.error(e.message));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (viewRental) return <AdminRentalDetail rid={viewRental} onBack={() => setViewRental(null)} />;
  if (!data) return <div className="text-gray-500 text-sm p-8 text-center">Loading…</div>;

  const { user, rentals, agents, broker } = data;
  const initials = user.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";

  return (
    <div className="space-y-5">
      {editing && (
        <EditUserModal user={user} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}

      {/* Nav */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </button>
        <button onClick={() => setEditing(true)}
          className="ml-auto text-sm bg-[#6366F1] hover:bg-[#4F46E5] text-white px-4 py-2 rounded-lg font-medium transition-colors">
          Edit Profile
        </button>
      </div>

      {/* Profile card */}
      <div className="card-base p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center text-xl font-bold text-[#F97316] flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
              <Badge status={user.role} />
              <Badge status={user.kyc_status || "none"} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
              <div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Phone</div>
                <div className="font-mono text-sm text-gray-700 mt-0.5">{user.phone}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Email</div>
                <div className="text-sm text-gray-700 mt-0.5 truncate">{user.email || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Joined</div>
                <div className="text-sm text-gray-700 mt-0.5">{fmtDate(user.created_at)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Language</div>
                <div className="text-sm text-gray-700 mt-0.5">{user.preferred_language?.toUpperCase() || "EN"}</div>
              </div>
              {user.business_name && (
                <div>
                  <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Business</div>
                  <div className="text-sm text-gray-700 mt-0.5">{user.business_name}</div>
                </div>
              )}
              {broker && (
                <div>
                  <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Under Broker</div>
                  <div className="text-sm text-gray-700 mt-0.5">{broker.name}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Broker's team */}
      {agents?.length > 0 && (
        <div className="card-base p-5">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">
            Team — {agents.length} agent{agents.length !== 1 ? "s" : ""}
          </div>
          <div className="space-y-2">
            {agents.map(a => (
              <div key={a.id} className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 flex-shrink-0">
                  {a.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{a.name}</div>
                  <div className="font-mono text-xs text-gray-400">{a.phone}</div>
                </div>
                <Badge status={a.kyc_status || "none"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rentals / Agreements */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">
            Agreements · Rentals ({rentals.length})
          </div>
        </div>
        {rentals.length === 0 ? (
          <div className="text-gray-400 text-sm text-center py-6">No rentals yet.</div>
        ) : (
          <div className="space-y-2">
            {rentals.map(r => (
              <button key={r.id} onClick={() => setViewRental(r.id)}
                className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-[#F97316]/30 hover:bg-orange-50/20 transition-all group">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">{r.property_address}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Deposit {formatINR(r.deposit_amount)} · {fmtDate(r.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge status={r.status} />
                    {r.dispute && <Badge status={r.dispute.status} />}
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-[#F97316] transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chatbot conversation history */}
      <ChatbotHistory userId={userId} />
    </div>
  );
}

// ─── Disputes ─────────────────────────────────────────────────────────────────
function ResolveModal({ dispute, onClose, onDone }) {
  const [deduction, setDeduction] = useState(Math.round((dispute.dispute?.orello_deduction_suggestion || 0) / 100));
  const [verdict, setVerdict]     = useState("");
  const [reasoning, setReasoning] = useState("");
  const [busy, setBusy]           = useState(false);
  const depositAmount = dispute.rental?.deposit_amount || 0;
  const refund = Math.max(0, depositAmount - deduction * 100);

  const submit = async () => {
    if (!verdict.trim()) { toast.error("Verdict is required"); return; }
    setBusy(true);
    try {
      await req("POST", `/admin/disputes/${dispute.dispute.id}/resolve`, {
        deduction_paise: deduction * 100, verdict: verdict.trim(), reasoning: reasoning.trim(),
      });
      toast.success("Resolved. Both parties notified.");
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card-base w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <div className="font-semibold text-gray-900">Set Final Resolution</div>
            <div className="text-xs text-gray-500 mt-0.5">{dispute.rental?.property_address}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {dispute.dispute?.orello_suggestion && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="text-xs font-semibold text-blue-600 uppercase mb-1">🤖 ORE-AI Suggestion</div>
              <div className="text-sm text-blue-800">{dispute.dispute.orello_suggestion}</div>
              <div className="text-xs text-blue-500 mt-1">Suggested deduction: {formatINR(dispute.dispute.orello_deduction_suggestion)}</div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700">Fair Deduction (₹)</label>
            <input type="number" value={deduction} onChange={e => setDeduction(Number(e.target.value))}
              min={0} max={depositAmount / 100}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Landlord gets: <strong className="text-red-600">{formatINR(deduction * 100)}</strong></span>
              <span>Tenant gets: <strong className="text-emerald-600">{formatINR(refund)}</strong></span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Verdict <span className="text-red-500">*</span></label>
            <textarea value={verdict} onChange={e => setVerdict(e.target.value)} rows={3}
              placeholder="Clear, fair summary shown to both parties…"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Detailed Reasoning</label>
            <textarea value={reasoning} onChange={e => setReasoning(e.target.value)} rows={4}
              placeholder="Item-by-item analysis, evidence reviewed, market rates applied…"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 resize-none" />
          </div>
          <button onClick={submit} disabled={busy}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-3 rounded-lg text-sm disabled:opacity-50 transition-colors">
            {busy ? "Submitting…" : "Submit Final Resolution"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DisputeDetail({ disputeId, onBack, onResolved }) {
  const [data, setData]         = useState(null);
  const [showResolve, setShowResolve] = useState(false);

  useEffect(() => {
    req("GET", `/admin/disputes/${disputeId}`).then(setData).catch(e => toast.error(e.message));
  }, [disputeId]);

  if (!data) return <div className="text-gray-500 text-sm p-8 text-center">Loading…</div>;
  const { dispute, rental, deduction, checkin, checkout, tenants, landlord } = data;

  return (
    <div className="space-y-5">
      {showResolve && <ResolveModal dispute={data} onClose={() => setShowResolve(false)}
        onDone={() => { setShowResolve(false); onResolved(); }} />}

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <Badge status={dispute.status} />
        <span className="text-gray-400 text-xs">{timeAgo(dispute.created_at)}</span>
        {["pending","ore_reviewed","manual_review"].includes(dispute.status) && (
          <button onClick={() => setShowResolve(true)}
            className="ml-auto bg-[#F97316] hover:bg-[#EA580C] text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors">
            Set Resolution
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card-base p-4 space-y-1">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Property</div>
          <div className="font-semibold text-gray-900">{rental?.property_address}</div>
          <div className="text-sm text-gray-500">{rental?.property_type} · Deposit {formatINR(rental?.deposit_amount)}</div>
        </div>
        <div className="card-base p-4 space-y-1">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Parties</div>
          {tenants?.map((t, i) => (
            <div key={i} className="text-sm text-gray-700">
              {t.user?.name || "Tenant"} — <span className="font-mono text-xs">{t.user?.phone}</span>
            </div>
          ))}
          <div className="text-sm text-gray-500">
            Landlord: {landlord?.name} — <span className="font-mono text-xs">{landlord?.phone}</span>
          </div>
        </div>
      </div>

      <div className="card-base p-4">
        <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">Tenant's Dispute Reason</div>
        <div className="text-gray-800 text-sm">{dispute.reason || "—"}</div>
        {dispute.evidence_notes && <div className="text-gray-500 text-sm mt-2 italic">Evidence: {dispute.evidence_notes}</div>}
      </div>

      {deduction?.items?.length > 0 && (
        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">Landlord's Claim</div>
          {deduction.items.map((item, i) => (
            <div key={i} className="flex justify-between text-sm py-2 border-b border-gray-50 last:border-0">
              <span className="text-gray-600">{item.room} — {item.reason}</span>
              <span className="font-medium text-gray-900">{formatINR(item.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm pt-2 font-semibold">
            <span className="text-gray-600">Total claimed</span>
            <span className="text-[#F97316]">{formatINR(deduction.total_deduction)}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {[["Check-in", checkin], ["Checkout", checkout]].map(([label, rep]) => (
          <div key={label} className="card-base p-4">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-2">{label} Report</div>
            <ReportView report={rep} />
          </div>
        ))}
      </div>

      {dispute.orello_suggestion && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="text-xs text-blue-600 font-semibold uppercase mb-2">🤖 ORE-AI Verdict</div>
          <div className="text-blue-800 text-sm">{dispute.orello_suggestion}</div>
          <div className="text-xs text-blue-500 mt-2">
            Deduction: {formatINR(dispute.orello_deduction_suggestion)} · Refund: {formatINR(dispute.orello_refund_suggestion)}
          </div>
        </div>
      )}

      {dispute.ore_ai_item_analysis?.length > 0 && (
        <div className="card-base p-4">
          <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-3">ORE-AI Item Analysis</div>
          <div className="grid grid-cols-3 text-xs text-gray-400 font-medium pb-2 border-b border-gray-100">
            <span>Item</span><span className="text-right">Claimed</span><span className="text-right">Deducted</span>
          </div>
          {dispute.ore_ai_item_analysis.map((a, i) => (
            <div key={i} className="grid grid-cols-3 py-2 border-b border-gray-50 last:border-0 text-sm">
              <span className="text-gray-700">{a.room ? `${a.room} — ` : ""}{a.item}</span>
              <span className="text-right text-gray-500">{formatINR(a.claimed)}</span>
              <span className={`text-right font-medium ${a.awarded === 0 ? "text-emerald-600" : a.awarded < a.claimed ? "text-amber-600" : "text-red-600"}`}>
                {a.awarded === 0 ? "Nil" : formatINR(a.awarded)}
              </span>
            </div>
          ))}
        </div>
      )}

      {dispute.manual_review_verdict && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="text-xs text-orange-600 font-semibold uppercase mb-2">Final Decision (Orello Team)</div>
          <div className="text-orange-800 text-sm font-medium">{dispute.manual_review_verdict}</div>
          {dispute.manual_review_reasoning && <div className="text-orange-600 text-xs mt-2">{dispute.manual_review_reasoning}</div>}
          <div className="text-xs text-gray-500 mt-2">
            Deduction: {formatINR(dispute.manual_review_deduction)} · Refund: {formatINR(dispute.manual_review_refund)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── External settlement disbursements ──────────────────────────────────────────
function DisbursementCard({ it, onDone }) {
  const ep = it.external_proof || {};
  const held = it.disputed_amount_held || 0;
  const [landlord, setLandlord] = useState("");
  const [tenant, setTenant]     = useState("");
  const [note, setNote]         = useState("");
  const [busy, setBusy]         = useState(false);
  const sumPaise = Math.round((Number(landlord || 0) + Number(tenant || 0)) * 100);

  const disburse = async () => {
    if (sumPaise <= 0) return toast.error("Enter the split to disburse.");
    if (sumPaise > held + 100) return toast.error(`Split exceeds the held amount (${formatINR(held)}).`);
    if (!window.confirm(`Disburse ${formatINR(Math.round(Number(landlord || 0) * 100))} to landlord and ${formatINR(Math.round(Number(tenant || 0) * 100))} to tenant?`)) return;
    setBusy(true);
    try {
      await req("POST", `/admin/disbursements/${it.dispute_id}/disburse`, {
        landlord_amount_paise: Math.round(Number(landlord || 0) * 100),
        tenant_amount_paise: Math.round(Number(tenant || 0) * 100),
        note,
      });
      toast.success("Disbursed. Both parties notified.");
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-base p-4 space-y-3">
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">{it.property_address || "—"}</div>
          <div className="text-xs text-gray-500 mt-0.5">Submitted by {ep.submitted_by_name || ep.submitted_by_role || "a party"} · {fmtDate(ep.submitted_at)}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-gray-400">Held (disputed)</div>
          <div className="font-bold text-amber-600">{formatINR(held)}</div>
        </div>
      </div>
      {ep.note && <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2.5">{ep.note}</div>}
      {ep.proof_urls?.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {ep.proof_urls.map((u, i) => (
            (u || "").toLowerCase().includes(".pdf") ? (
              <a key={i} href={u} target="_blank" rel="noreferrer"
                className="h-20 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center gap-1 text-xs text-gray-500">
                <FileText size={20} className="text-red-400" /> PDF
              </a>
            ) : (
              <a key={i} href={u} target="_blank" rel="noreferrer">
                <img src={u} alt="proof" className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:opacity-90" />
              </a>
            )
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-xs text-gray-500 mb-1">Landlord receives (₹)</div>
          <input type="number" value={landlord} onChange={e => setLandlord(e.target.value)} placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Tenant receives (₹)</div>
          <input type="number" value={tenant} onChange={e => setTenant(e.target.value)} placeholder="0"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>
      <div className={`text-xs ${sumPaise > held + 100 ? "text-red-500" : "text-gray-500"}`}>
        Total split: {formatINR(sumPaise)} of {formatINR(held)} held{sumPaise > held + 100 ? " — exceeds held amount" : ""}
      </div>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Admin note (optional)"
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" />
      <button onClick={disburse} disabled={busy}
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50">
        {busy ? "Disbursing…" : "Validate & disburse"}
      </button>
    </div>
  );
}

function Disbursements() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    req("GET", "/admin/disbursements").then(d => setItems(d.disbursements || [])).catch(e => toast.error(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">External Settlement Disbursements</h2>
        <p className="text-sm text-gray-500 mt-1">Parties who resolved externally and submitted proof. Validate the proof and disburse the held amount.</p>
      </div>
      {items === null ? (
        <div className="card-base p-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-base p-12 text-center text-gray-400 text-sm">No pending disbursement requests.</div>
      ) : (
        <div className="space-y-4">
          {items.map(it => <DisbursementCard key={it.dispute_id} it={it} onDone={load} />)}
        </div>
      )}
    </div>
  );
}

function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [filter, setFilter]     = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    req("GET", `/admin/disputes${filter ? `?status=${filter}` : ""}`)
      .then(setDisputes).catch(e => toast.error(e.message));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (selected) return (
    <DisputeDetail disputeId={selected}
      onBack={() => { setSelected(null); load(); }}
      onResolved={() => { setSelected(null); load(); }} />
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold font-display">Dispute Cases</h1>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 bg-white">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="ore_reviewed">ORE-AI Reviewed</option>
          <option value="manual_review">Manual Review</option>
          <option value="manual_resolved">Manual Resolved</option>
          <option value="resolved">Resolved</option>
          <option value="external">External</option>
        </select>
      </div>
      {disputes.length === 0 ? (
        <div className="card-base p-12 text-center text-gray-400 text-sm">No disputes found.</div>
      ) : (
        <div className="space-y-2">
          {disputes.map(d => (
            <button key={d.id} onClick={() => setSelected(d.id)}
              className="w-full text-left card-base p-4 hover:border-[#F97316]/30 transition-all group">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge status={d.status} />
                  <span className="font-medium text-gray-900 text-sm truncate">{d.rental?.property_address || d.rental_id}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-gray-400 text-xs">{timeAgo(d.created_at)}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-[#F97316] transition-colors" />
                </div>
              </div>
              <div className="text-gray-500 text-xs mt-1.5">{d.raised_by_user?.name || "Tenant"} · {d.reason?.slice(0, 80)}</div>
              {d.orello_deduction_suggestion != null && (
                <div className="text-xs text-blue-500 mt-1">🤖 ORE-AI: {formatINR(d.orello_deduction_suggestion)} deduction</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Users ─────────────────────────────────────────────────────────────────────
function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "", email: user.email || "",
    phone: user.phone || "", kyc_status: user.kyc_status || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await req("PATCH", `/admin/users/${user.id}`, form);
      toast.success("User updated.");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card-base w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="font-semibold text-gray-900">Edit User</div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {[["Full Name", "name", "text"], ["Email", "email", "email"], ["Phone", "phone", "text"]].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-sm font-medium text-gray-700">{label}</label>
              <input type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
            </div>
          ))}
          <div>
            <label className="text-sm font-medium text-gray-700">KYC Status</label>
            <select value={form.kyc_status} onChange={e => setForm({ ...form, kyc_status: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 bg-white">
              <option value="">Not started</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <button onClick={save} disabled={busy}
            className="w-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-medium py-2.5 rounded-lg text-sm disabled:opacity-50 transition-colors">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "tenant", password: "Orello@123" });
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  const phoneDigits = (v) => v.replace(/^\+91/, "").replace(/^91(?=\d{10}$)/, "").replace(/\D/g, "");

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const digits = phoneDigits(form.phone);
    if (!/^[6-9]\d{9}$/.test(digits)) { toast.error("Enter a valid 10-digit Indian mobile number"); return; }
    if (!form.password.trim()) { toast.error("Password is required"); return; }
    setBusy(true);
    try {
      await req("POST", "/admin/users", { ...form, phone: `+91${digits}` });
      toast.success("User created successfully.");
      onCreated();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card-base w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="font-semibold text-gray-900">Create New User</div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Account Type</label>
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 bg-white">
              <option value="tenant">Tenant</option>
              <option value="landlord">Landlord</option>
              <option value="agent">Agent</option>
              <option value="broker_admin">Broker Admin</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Rahul Sharma"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone <span className="text-red-500">*</span></label>
            <div className="mt-1 flex">
              <span className="px-3 py-2.5 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-500 flex-shrink-0">+91</span>
              <input type="tel" maxLength={10}
                value={form.phone.replace(/^\+?91?/, "").replace(/\D/g, "")}
                onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })}
                placeholder="10-digit number"
                className="flex-1 border border-gray-300 rounded-r-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="user@example.com"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Password</label>
            <input type={showPass ? "text" : "password"} value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
            <button type="button" onClick={() => setShowPass(p => !p)}
              className="text-xs text-indigo-500 mt-1 hover:text-indigo-700">
              {showPass ? "Hide" : "Show"} password
            </button>
          </div>
          <button onClick={create} disabled={busy}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-2.5 rounded-lg text-sm disabled:opacity-50 transition-colors">
            {busy ? "Creating…" : "Create User"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersList() {
  const [users, setUsers]       = useState([]);
  const [search, setSearch]     = useState("");
  const [roleFilter, setRole]   = useState("");
  const [editing, setEditing]   = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing]   = useState(null);   // userId for UserDetail

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (roleFilter) p.set("role", roleFilter);
    req("GET", `/admin/users?${p}`).then(setUsers).catch(e => toast.error(e.message));
  }, [search, roleFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  if (viewing) return <UserDetail userId={viewing} onBack={() => { setViewing(null); load(); }} />;

  return (
    <div>
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <CreateUserModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold font-display">User Management</h1>
        <button onClick={() => setCreating(true)}
          className="flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors">
          <Plus className="h-4 w-4" /> Create User
        </button>
      </div>

      <div className="flex gap-3 mb-5">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone or email…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500" />
        </div>
        <select value={roleFilter} onChange={e => setRole(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 bg-white">
          <option value="">All roles</option>
          <option value="tenant">Tenant</option>
          <option value="landlord">Landlord</option>
          <option value="agent">Agent</option>
          <option value="broker_admin">Broker Admin</option>
        </select>
      </div>

      <div className="text-xs text-gray-400 mb-3">{users.length} users</div>

      <div className="card-base overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Name</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Phone</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Email</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Role</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">KYC</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Rentals</th>
              <th className="text-left px-4 py-3 text-xs text-gray-500 font-semibold uppercase tracking-wide">Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map(u => (
              <tr key={u.id}
                onClick={() => setViewing(u.id)}
                className="hover:bg-orange-50/30 transition-colors cursor-pointer group">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-xs font-bold text-[#F97316] flex-shrink-0">
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <span className="font-medium text-gray-900 group-hover:text-[#F97316] transition-colors">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.phone}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{u.email || "—"}</td>
                <td className="px-4 py-3"><Badge status={u.role} /></td>
                <td className="px-4 py-3"><Badge status={u.kyc_status || "none"} /></td>
                <td className="px-4 py-3 text-gray-500">{u.rental_count || 0}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); setViewing(u.id); }}
                      className="text-gray-400 hover:text-[#F97316] transition-colors" title="View profile">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); setEditing(u); }}
                      className="text-[#6366F1] hover:text-[#4F46E5] text-xs font-medium transition-colors">
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <div className="text-center text-gray-400 text-sm py-12">No users found.</div>}
      </div>
    </div>
  );
}

// ─── Support ──────────────────────────────────────────────────────────────────
function TicketModal({ ticket, onClose, onUpdated }) {
  const [reply, setReply]   = useState("");
  const [status, setStatus] = useState(ticket.status || "open");
  const [busy, setBusy]     = useState(false);

  const send = async () => {
    if (!reply.trim()) { toast.error("Enter a reply"); return; }
    setBusy(true);
    try {
      await req("POST", `/admin/support/${ticket.id}/reply`, { message: reply.trim(), status });
      toast.success("Reply sent.");
      setReply(""); onUpdated();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const markResolved = async () => {
    await req("PATCH", `/admin/support/${ticket.id}`, { status: "resolved" });
    toast.success("Marked as resolved."); onUpdated();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card-base w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <div className="font-semibold text-gray-900">{ticket.subject}</div>
            <div className="text-xs text-gray-400 mt-0.5">{ticket.name} · {ticket.phone || ticket.email} · {timeAgo(ticket.created_at)}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge status={status} />
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 ml-1"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-xs text-gray-400 mb-1">User message</div>
            <div className="text-gray-800 text-sm">{ticket.message}</div>
          </div>
          {ticket.replies?.map((r, i) => (
            <div key={i} className="bg-[#FFF4ED] border border-orange-100 rounded-xl p-4">
              <div className="text-xs text-[#F97316] mb-1">{r.by} · {timeAgo(r.at)}</div>
              <div className="text-gray-800 text-sm">{r.message}</div>
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-gray-100 space-y-3">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3}
            placeholder="Type your reply…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 resize-none" />
          <div className="flex gap-2">
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white">
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
            <button onClick={send} disabled={busy}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white font-medium py-2 rounded-lg text-sm disabled:opacity-50 transition-colors">
              {busy ? "Sending…" : "Send Reply"}
            </button>
            <button onClick={markResolved}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <CheckCircle2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Support() {
  const [tickets, setTickets]   = useState([]);
  const [filter, setFilter]     = useState("open");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    req("GET", `/admin/support${filter ? `?status=${filter}` : ""}`)
      .then(setTickets).catch(e => toast.error(e.message));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const selectedTicket = tickets.find(t => t.id === selected);

  return (
    <div>
      {selectedTicket && <TicketModal ticket={selectedTicket} onClose={() => setSelected(null)}
        onUpdated={() => { setSelected(null); load(); }} />}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold font-display">Support Tickets</h1>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 bg-white">
          <option value="">All</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
      {tickets.length === 0 ? (
        <div className="card-base p-12 text-center text-gray-400 text-sm">No tickets found.</div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <button key={t.id} onClick={() => setSelected(t.id)}
              className="w-full text-left card-base p-4 hover:border-[#F97316]/30 transition-all group">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge status={t.status} />
                  <span className="font-medium text-gray-900 text-sm truncate">{t.subject}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-gray-400 text-xs">{timeAgo(t.created_at)}</span>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-[#F97316] transition-colors" />
                </div>
              </div>
              <div className="text-gray-500 text-xs mt-1">{t.name} · {t.phone || t.email}</div>
              <div className="text-gray-400 text-xs mt-0.5 truncate">{t.message}</div>
              {t.replies?.length > 0 && (
                <div className="text-xs text-[#F97316] mt-1">{t.replies.length} {t.replies.length === 1 ? "reply" : "replies"}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Feedback ─────────────────────────────────────────────────────────────────
function FeedbackSection() {
  const [items, setItems]   = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    req("GET", `/admin/feedback${filter ? `?type=${filter}` : ""}`)
      .then(setItems).catch(e => toast.error(e.message));
  }, [filter]);

  const score = (item) => {
    const ans = item.answers || [];
    if (!ans.length) return null;
    const yes = ans.filter(a => a.answer === true).length;
    return Math.round((yes / ans.length) * 100);
  };

  const expanded = selected ? items.find(i => i.id === selected) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-display">User Feedback</h1>
          <p className="text-sm text-gray-500 mt-0.5">{items.length} response{items.length !== 1 ? "s" : ""}</p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 bg-white">
          <option value="">All types</option>
          <option value="onboarding">Onboarding</option>
          <option value="case_closed">Case Closed</option>
        </select>
      </div>

      {/* Summary stats */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {["onboarding", "case_closed"].map(t => {
            const group = items.filter(i => i.type === t);
            if (!group.length) return null;
            const avg = Math.round(group.map(score).filter(s => s !== null).reduce((a, b) => a + b, 0) / group.length);
            return (
              <div key={t} className="card-base p-4">
                <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">
                  {t === "onboarding" ? "Onboarding" : "Case Closed"}
                </div>
                <div className="text-3xl font-bold font-display text-[#F97316] mt-1">{avg}%</div>
                <div className="text-xs text-gray-500 mt-0.5">positive · {group.length} response{group.length !== 1 ? "s" : ""}</div>
              </div>
            );
          }).filter(Boolean)}
          <div className="card-base p-4">
            <div className="text-xs text-gray-400 uppercase font-semibold tracking-wide">Overall</div>
            <div className="text-3xl font-bold font-display text-emerald-600 mt-1">
              {Math.round(items.map(score).filter(s => s !== null).reduce((a, b) => a + b, 0) / items.length)}%
            </div>
            <div className="text-xs text-gray-500 mt-0.5">positive · {items.length} total</div>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="card-base p-12 text-center text-gray-400 text-sm">No feedback submitted yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const pct = score(item);
            return (
              <button key={item.id} onClick={() => setSelected(selected === item.id ? null : item.id)}
                className="w-full text-left card-base p-4 hover:border-[#F97316]/30 transition-all">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                      item.type === "onboarding"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-purple-50 text-purple-700 border-purple-200"
                    }`}>
                      {item.type === "onboarding" ? "Onboarding" : "Case Closed"}
                    </span>
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900">{item.user_name}</span>
                      <span className="text-xs text-gray-400 ml-2">{item.user_role}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {pct !== null && (
                      <span className={`text-sm font-bold ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-red-600"}`}>
                        {pct}% ✓
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{timeAgo(item.created_at)}</span>
                    <ChevronRight className={`h-4 w-4 text-gray-300 transition-transform ${selected === item.id ? "rotate-90" : ""}`} />
                  </div>
                </div>

                {/* Expanded detail */}
                {selected === item.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 text-left">
                    {/* Q&A */}
                    <div className="space-y-2">
                      {item.answers?.map((a, i) => (
                        <div key={i} className="flex items-start justify-between gap-3">
                          <span className="text-xs text-gray-600 flex-1">{a.question}</span>
                          <span className={`flex items-center gap-1 text-xs font-medium flex-shrink-0 ${a.answer ? "text-emerald-600" : "text-red-500"}`}>
                            {a.answer
                              ? <><ThumbsUp className="h-3 w-3" /> Yes</>
                              : <><ThumbsDown className="h-3 w-3" /> No</>
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* Comments */}
                    {item.comments && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2.5">
                        <div className="text-xs text-gray-400 mb-1">Comment</div>
                        <div className="text-sm text-gray-700 italic">"{item.comments}"</div>
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>📱 {item.user_phone}</span>
                      {item.rental_id && <span>Deal #{item.rental_id.slice(-6)}</span>}
                      <span>{fmtDate(item.created_at)}</span>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function AdminApp() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [stats, setStats]   = useState(null);
  const [ready, setReady]   = useState(false);

  useEffect(() => {
    req("GET", "/admin/stats")
      .then(s => { setStats(s); setReady(true); })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate]);

  if (!ready) return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--dash-bg)", color: "var(--dash-text-2)", fontSize: 14,
    }}>
      Loading…
    </div>
  );

  const logout = () => { localStorage.removeItem(TOKEN_KEY); navigate("/login", { replace: true }); };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--dash-bg)", color: "var(--dash-text)" }}>
      <AdminSidebar active={active} setActive={setActive} onLogout={logout} stats={stats} />
      <main style={{ flex: 1, padding: 32, maxWidth: 1180, width: "100%", overflowY: "auto" }}>
        {active === "overview"  && <Overview stats={stats} setActive={setActive} />}
        {active === "disputes"  && <Disputes />}
        {active === "disbursements" && <Disbursements />}
        {active === "users"     && <UsersList />}
        {active === "support"   && <Support />}
        {active === "feedback"  && <FeedbackSection />}
        {active === "editor"    && <EditPageEditor />}
      </main>
    </div>
  );
}
